import { getLimitsMode } from "./rewardsConstants";

export const FIELD_LABEL = {
  name: "name",
  desc: "reward history name",
  coins: "coins",
  dailyLimit: "daily limit",
  monthlyLimit: "monthly limit",
  cooldownDays: "cooldown",
  expiryDays: "expiry",
};

export const FREQUENCY_LABELS = {
  one_time: "One-time only",
  unlimited: "Unlimited",
  count_cap: "Daily/monthly limit",
  cooldown: "Cooldown period",
};

const COUNT_CAP_FREQUENCIES = new Set([
  "DAILY_MONTHLY",
  "DAILY_MONTHLY_LIMIT",
  "LIMIT",
  "LIMITED",
  "COUNT",
]);

export function frequencyTypeFromEarningFrequency(freq) {
  const normalized = (freq || "").toUpperCase();
  if (normalized === "ONCE") return "one_time";
  if (normalized === "UNLIMITED") return "unlimited";
  if (normalized === "COOLDOWN") return "cooldown";
  if (COUNT_CAP_FREQUENCIES.has(normalized)) return "count_cap";
  return null;
}

export function getFrequencyType(cfg) {
  return frequencyTypeFromEarningFrequency(cfg.earningFrequency) || frequencyTypeOf(cfg);
}

export function frequencyTypeOf(cfg) {
  if (cfg.oneTime) return "one_time";
  if (cfg.cooldownDays != null) return "cooldown";
  if (cfg.dailyLimit != null || cfg.monthlyLimit != null) return "count_cap";
  return "unlimited";
}

export function validateEventCaps(cfg) {
  const warnings = [];
  const mode = getLimitsMode(cfg);

  if (mode !== "daily_monthly" && mode !== "daily") return warnings;

  if (mode === "daily_monthly" || mode === "daily") {
    if (cfg.dailyLimit == null || cfg.dailyLimit === "") {
      warnings.push("Daily limit is required for daily/monthly mode.");
    }
  }

  if (mode === "daily_monthly") {
    if (cfg.monthlyLimit == null || cfg.monthlyLimit === "") {
      warnings.push("Monthly limit is required for daily/monthly mode.");
    } else if (
      cfg.dailyLimit != null &&
      cfg.dailyLimit !== "" &&
      Number(cfg.monthlyLimit) <= Number(cfg.dailyLimit)
    ) {
      warnings.push(
        `Monthly limit must be greater than daily limit (${cfg.dailyLimit}).`
      );
    }
  }

  return warnings;
}

export function validateEventExpiry(cfg) {
  const warnings = [];
  const raw = cfg.expiryDays;
  if (raw === "" || raw == null) {
    warnings.push("Expiry must be at least 1 day.");
    return warnings;
  }
  const digits = normalizeCoinDraft(raw);
  if (digits === "") {
    warnings.push("Expiry must be at least 1 day.");
    return warnings;
  }
  const num = Math.round(Number(digits));
  if (Number.isNaN(num) || num < 1) {
    warnings.push("Expiry must be at least 1 day.");
  }
  return warnings;
}

export function finalizeExpiryDays(value) {
  if (value === "" || value == null) return null;
  return parseWholeNumber(normalizeCoinDraft(value), { min: 1 });
}

export function finalizeDailyLimit(value) {
  if (value === "" || value == null) return null;
  return parseWholeNumber(normalizeCoinDraft(value), { min: 0 });
}

export function finalizeMonthlyLimit(value, dailyLimit) {
  if (value === "" || value == null) return null;
  let monthly = parseWholeNumber(normalizeCoinDraft(value), { min: 1 });
  if (dailyLimit != null && monthly <= dailyLimit) {
    monthly = dailyLimit + 1;
  }
  return monthly;
}

export function applyEventLimitBlur(cfg, field, rawValue) {
  const next = { ...cfg };
  const mode = getLimitsMode(cfg);

  if (field === "dailyLimit") {
    next.dailyLimit = finalizeDailyLimit(rawValue);
    if (
      mode === "daily_monthly" &&
      next.dailyLimit != null &&
      next.monthlyLimit !== "" &&
      next.monthlyLimit != null
    ) {
      next.monthlyLimit = finalizeMonthlyLimit(next.monthlyLimit, next.dailyLimit);
    }
  } else if (field === "monthlyLimit") {
    const daily = finalizeDailyLimit(next.dailyLimit);
    if (mode === "daily" || mode === "daily_monthly") {
      next.dailyLimit = daily;
    }
    next.monthlyLimit = finalizeMonthlyLimit(rawValue, daily);
  }

  next.version = (cfg.version || 1) + 1;
  return next;
}

export function validateEvent(cfg) {
  const warnings = [];
  if (!(cfg.desc || "").trim()) {
    warnings.push("Reward History Name is required.");
  }
  return [...warnings, ...validateEventCaps(cfg), ...validateEventExpiry(cfg)];
}

export function deriveLevelStatus(coins, currentStatus) {
  if (coins === 0 && currentStatus === "active") return "paused";
  return currentStatus;
}

export function validateReferralLevel(level) {
  const warnings = [];
  const coins = coinDraftToNumber(level.coins);
  if (coins < 0) warnings.push("Coins cannot be negative.");
  if (level.status === "active" && coins <= 0) {
    warnings.push("Active with 0 coins — this level will never pay out.");
  }
  const firstShiftBonus =
    level.firstShiftBonus === "" || level.firstShiftBonus == null
      ? null
      : coinDraftToNumber(level.firstShiftBonus);
  if (level.firstShiftExpiryDays != null && firstShiftBonus == null) {
    warnings.push(
      "First-shift expiry is set but First-shift bonus is blank — expiry has nothing to apply to."
    );
  }
  if (level.notificationEnabled) {
    if (!(level.notificationTitle || "").trim() || !(level.notificationBody || "").trim()) {
      warnings.push("Notification title and body are required when enabled.");
    }
  }
  return warnings;
}

export function hasModuleAction(adminData, moduleName, action) {
  if (adminData?.isSuperAdmin) return true;
  return Boolean(
    adminData?.permissions?.some(
      (item) => item.module === moduleName && item.actions?.includes(action)
    )
  );
}

export function getAdminDisplayName(adminData) {
  if (!adminData) return "Admin";
  if (typeof adminData.name === "string" && adminData.name.trim()) return adminData.name;
  if (adminData.name?.first || adminData.name?.last) {
    return [adminData.name.first, adminData.name.last].filter(Boolean).join(" ");
  }
  if (typeof adminData.userName === "string" && adminData.userName.trim()) {
    return adminData.userName;
  }
  return "Admin";
}

export function fmtTime(date) {
  if (!date) return "";
  const d = date instanceof Date ? date : new Date(date);
  return d.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function parseWholeNumber(value, { min = 0, max = null, allowBlank = false } = {}) {
  if (value === "" || value === null || value === undefined) {
    return allowBlank ? null : min;
  }
  let num = Math.round(Number(value));
  if (Number.isNaN(num)) num = min;
  num = Math.max(min, num);
  if (max != null) num = Math.min(max, num);
  return num;
}

/** Digits-only coin draft; strips leading zeros ("02" → "2") but keeps a lone "0". */
export function normalizeCoinDraft(value) {
  if (value === "" || value == null) return "";
  const digits = String(value).replace(/\D/g, "");
  if (digits === "") return "";
  return digits.replace(/^0+(?=\d)/, "");
}

export function coinDraftToNumber(value) {
  if (value === "" || value == null) return 0;
  return parseWholeNumber(normalizeCoinDraft(value), { min: 0 });
}
